import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useRef, useState } from 'react';

/** localStorage key + bounds for the focused-view left column width (px). */
export const LEFT_COL_WIDTH_KEY = 'focused-view:left-col-width';
export const DEFAULT_LEFT_COL_WIDTH = 340;
export const MIN_LEFT_COL_WIDTH = 260;
export const MAX_LEFT_COL_WIDTH = 560;

export type ColumnResizeHandleProps = {
	/** Current column width in px — the drag baseline. */
	width: number;
	/** Called with the clamped new width during a drag / on double-click reset. */
	onResize: (width: number) => void;
};

/**
 * Right-edge drag handle for the left column. Mirrors the AppDrawer resize
 * pattern; the drag sign is inverted (drag right widens) since the handle is on
 * the column's right edge rather than a right-docked drawer's left edge.
 */
export const ColumnResizeHandle: FC<ColumnResizeHandleProps> = ({ width, onResize }) => {
	const [dragging, setDragging] = useState(false);
	const startXRef = useRef(0);
	const startWidthRef = useRef(0);

	const onMouseDown = (e: React.MouseEvent) => {
		e.preventDefault();
		startXRef.current = e.clientX;
		startWidthRef.current = width;
		setDragging(true);
	};

	useEffect(() => {
		if (!dragging) return;

		const handleMove = (e: MouseEvent) => {
			const delta = e.clientX - startXRef.current;
			const next = Math.min(Math.max(startWidthRef.current + delta, MIN_LEFT_COL_WIDTH), MAX_LEFT_COL_WIDTH);
			onResize(next);
		};
		const handleUp = () => setDragging(false);

		// Suppress text selection / flip the cursor for the whole drag — mousedown's
		// preventDefault doesn't stop selection across the subsequent mousemoves.
		const prevUserSelect = document.body.style.userSelect;
		const prevCursor = document.body.style.cursor;
		document.body.style.userSelect = 'none';
		document.body.style.cursor = 'col-resize';

		document.addEventListener('mousemove', handleMove);
		document.addEventListener('mouseup', handleUp);
		return () => {
			document.removeEventListener('mousemove', handleMove);
			document.removeEventListener('mouseup', handleUp);
			document.body.style.userSelect = prevUserSelect;
			document.body.style.cursor = prevCursor;
		};
	}, [dragging, onResize]);

	return (
		<div
			className={css(styles.handle, dragging && styles.handleDragging)}
			onMouseDown={onMouseDown}
			onDoubleClick={() => onResize(DEFAULT_LEFT_COL_WIDTH)}
			role='separator'
			aria-orientation='vertical'
			aria-label='Resize lead panel'
		/>
	);
};

const styles = StyleSheet.create({
	handle: {
		position: 'absolute',
		top: 0,
		bottom: 0,
		right: -3,
		width: 6,
		cursor: 'col-resize',
		background: 'transparent',
		zIndex: 2,
		transition: 'background 120ms',
		':hover': {
			background: 'var(--ring)',
		},
	},
	handleDragging: {
		background: 'var(--ring)',
	},
});
